import React from "react";

export default function Terms() {
  return (
    <div className="container py-5">

      <h1 className="fw-bold mb-4">Terms & Conditions</h1>

      <p className="text-muted" style={{ fontSize: "16px" }}>
        These Terms & Conditions ("Terms") govern your access to and use of SWIFTSHIP's website,
        logistics services, freight handling, and related customer support. By using any SWIFTSHIP
        service, you agree to these Terms. Please read them carefully. If you do not agree, do not
        use our services.
      </p>

      <hr className="my-4" />

      {/* 1 */}
      <h4 className="fw-bold">1. Acceptance of Terms</h4>
      <p className="text-muted">
        By creating an account, placing an order, or otherwise using SWIFTSHIP services you confirm
        that you are authorized to act on behalf of the entity or person for whom the service is
        provided and that you accept and will comply with these Terms.
      </p>

      <hr className="my-4" />

      {/* 2 */}
      <h4 className="fw-bold">2. Services Provided</h4>
      <p className="text-muted">
        SWIFTSHIP provides logistics solutions including domestic & international shipping, freight
        forwarding, customs clearance assistance, warehousing, e-commerce fulfillment, and related
        services. Specific service terms (prices, transit options, delivery promises) are provided
        at the time of booking.
      </p>

      <hr className="my-4" />

      {/* 3 */}
      <h4 className="fw-bold">3. Shipping Restrictions & Prohibited Items</h4>
      <p className="text-muted">
        Certain items are restricted or prohibited and cannot be shipped without pre-approval. These
        include (but are not limited to):
      </p>
      <ul className="text-muted">
        <li>✔ Hazardous materials, flammable liquids, explosives (unless pre-approved and handled under special procedures)</li>
        <li>✔ Illegal, counterfeit, or sanctioned goods</li>
        <li>✔ Perishable goods without prior written arrangement</li>
        <li>✔ Items prohibited by carriers, customs, or local law</li>
      </ul>
      <p className="text-muted">
        You are responsible for declaring accurate contents and value. SWIFTSHIP reserves the right
        to refuse shipment, detain or return shipments that violate these restrictions.
      </p>

      <hr className="my-4" />

      {/* 4 */}
      <h4 className="fw-bold">4. Delivery Timeframes & Expectations</h4>
      <p className="text-muted">
        Logistics are subject to many external factors. As an operational expectation:
      </p>
      <ul className="text-muted">
        <li>✔ <strong>Standard delivery timelines may take approximately 30–45 days or longer</strong> depending on customs, carrier routing, and operational conditions.</li>
        <li>✔ Delays may occur due to customs inspections, carrier schedules, weather, high-season congestion, or government procedures.</li>
        <li>✔ Extended transit times do not automatically constitute service failure or grounds for refund or chargeback.</li>
      </ul>
      <p className="text-muted">
        We will provide tracking and status updates where applicable. Customers should plan accordingly for extended lead times.
      </p>

      <hr className="my-4" />

      {/* 5 */}
      <h4 className="fw-bold">5. Pricing, Payment & Billing</h4>
      <p className="text-muted">
        Prices and fees are displayed during booking. Payment terms:
      </p>
      <ul className="text-muted">
        <li>✔ All fees are payable in the method and timeframe specified at checkout or invoice.</li>
        <li>✔ SWIFTSHIP uses third-party, PCI-DSS compliant payment processors to handle card transactions; SWIFTSHIP does not store full card numbers.</li>
        <li>✔ Fees are generally non-refundable once a shipping label, airway bill, or freight booking is generated.</li>
        <li>✔ Additional charges (storage, re-delivery, customs duties, inspections, corrections) may apply and will be invoiced to the customer.</li>
      </ul>
      <p className="text-muted">
        Failure to pay invoiced charges may result in withholding of shipments, collection actions, and reporting to credit or payment processors.
      </p>

      <hr className="my-4" />

      {/* 6 */}
      <h4 className="fw-bold">6. Cancellation & Refunds</h4>
      <p className="text-muted">
        Cancellation is permitted only if the shipment has not yet been picked up, no carrier assignment
        has occurred, and no customs documentation has been submitted. Refunds (if applicable) follow
        our Refund Policy and are subject to verification and processing time.
      </p>

      <hr className="my-4" />

      {/* 7 */}
      <h4 className="fw-bold">7. Liability, Insurance & Claims</h4>
      <p className="text-muted">
        SWIFTSHIP partners with licensed carriers and service providers. Liability for loss or damage is
        typically limited to the carrier’s published terms unless additional insurance is purchased.
      </p>
      <ul className="text-muted">
        <li>✔ Customers are responsible for proper packaging, labeling, and accurate documentation.</li>
        <li>✔ Claims for loss/damage must be filed within the timeframe specified by the carrier and SWIFTSHIP’s claims process; supporting documentation (photos, invoices, tracking) will be required.</li>
        <li>✔ SWIFTSHIP is not responsible for delays or losses caused by customs, government actions, or third-party carriers beyond our control.</li>
      </ul>

      <hr className="my-4" />

      {/* 8 */}
      <h4 className="fw-bold">8. Chargeback & Dispute Prevention</h4>
      <p className="text-muted">
        To reduce fraudulent disputes and protect both parties, the following applies:
      </p>
      <ul className="text-muted">
        <li>✔ Customers must contact SWIFTSHIP first to attempt resolution prior to initiating any chargeback with their bank or card issuer.</li>
        <li>✔ Chargebacks based on delivery delays that fall within the disclosed timeframe (30–45+ days) or caused by customs, carrier routing, or other external factors will be contested and may be deemed invalid.</li>
        <li>✔ False or fraudulent chargebacks may result in account suspension, pursuit of recovery (including legal action), and evidence submission to payment processors.</li>
        <li>✔ SWIFTSHIP logs tracking events, timestamps, communications, IP/device data, and proof-of-delivery records to support dispute resolution.</li>
      </ul>

      <hr className="my-4" />

      {/* 9 */}
      <h4 className="fw-bold">9. Compliance With Laws & Regulatory Requirements</h4>
      <p className="text-muted">
        You agree to comply with all applicable laws, export/import regulations, customs requirements,
        and sanctions. SWIFTSHIP may provide documentation to customs or government agencies where legally required.
      </p>

      <hr className="my-4" />

      {/* 10 */}
      <h4 className="fw-bold">10. Privacy & Data Use</h4>
      <p className="text-muted">
        We collect and use personal information as described in our Privacy Policy to provide services,
        comply with customs and legal requirements, detect fraud, and improve operations. By using our
        services you consent to that processing. (See Privacy Policy for full details.)
      </p>

      <hr className="my-4" />

      {/* 11 */}
      <h4 className="fw-bold">11. Limitation of Liability</h4>
      <p className="text-muted">
        To the maximum extent permitted by law, SWIFTSHIP and its affiliates will not be liable for
        indirect, consequential, special, incidental or punitive damages, or loss of profits arising out
        of or related to the services, even if advised of the possibility of such damages. Our total
        aggregate liability for direct damages shall not exceed the fees actually paid by the customer
        for the specific shipment that gave rise to the claim.
      </p>

      <hr className="my-4" />

      {/* 12 */}
      <h4 className="fw-bold">12. Indemnification</h4>
      <p className="text-muted">
        You agree to indemnify, defend and hold harmless SWIFTSHIP, its affiliates and partners from any
        claims, losses, liabilities, damages, costs and expenses (including legal fees) arising from
        your violation of these Terms, inaccurate shipping information, or illegal shipments.
      </p>

      <hr className="my-4" />

      {/* 13 */}
      <h4 className="fw-bold">13. Governing Law & Dispute Resolution</h4>
      <p className="text-muted">
        These Terms are governed by the laws of the State of Arizona, U.S.A. Parties agree to first
        attempt informal resolution. If unresolved, disputes shall be submitted to the courts in
        Maricopa County, Arizona. Users waive the right to participate in class-action litigation
        against SWIFTSHIP related to these services.
      </p>

      <hr className="my-4" />

      {/* 14 */}
      <h4 className="fw-bold">14. Changes to Terms</h4>
      <p className="text-muted">
        SWIFTSHIP may update these Terms periodically to reflect legal, operational or regulatory
        changes. Updated Terms will be posted on this page with the revised effective date.
      </p>

      <hr className="my-4" />

      {/* 15 */}
      <h4 className="fw-bold">15. Contact Information</h4>
      <p className="text-muted">
        For questions about these Terms, disputes, claims, or legal notices, contact:
      </p>
      <p className="mt-2">
        <strong>Email:</strong> info@swiftship.us <br />
        <strong>Address:</strong> 3800 Cabell Dr, Irving, TX 75063, United States <br />
        <strong>Phone:</strong> (480) 520-0274
      </p>

    </div>
  );
}
